/**
 * Reads a server-sent event stream from a fetch response.
 *
 * Both Archi engines answer this way, and neither can be read with
 * EventSource: it cannot send the Auth0 Bearer header. So the response is
 * fetched with the token and its frames are parsed here, one callback per event.
 */
export async function readEventStream(
  resp: Response,
  onEvent: (event: string, data: unknown) => void
): Promise<void> {
  if (!resp.ok || !resp.body) {
    let message = `HTTP ${resp.status}`;
    try {
      const j = await resp.json();
      if (j?.message) message = j.message;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";

  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    // SSE frames are separated by a blank line.
    let sepIdx = buffer.indexOf("\n\n");
    while (sepIdx !== -1) {
      const frame = buffer.slice(0, sepIdx);
      buffer = buffer.slice(sepIdx + 2);

      let eventName = "message";
      const dataLines: string[] = [];
      for (const line of frame.split("\n")) {
        if (line.startsWith("event:")) {
          eventName = line.slice(6).trim();
        } else if (line.startsWith("data:")) {
          dataLines.push(line.slice(5).trim());
        }
      }
      if (dataLines.length) {
        try {
          onEvent(eventName, JSON.parse(dataLines.join("\n")));
        } catch {
          onEvent(eventName, { raw: dataLines.join("\n") });
        }
      }
      sepIdx = buffer.indexOf("\n\n");
    }
  }
}
